/**
 * Stage 1 orchestrator — builds a DocumentIR from a DocxPackage.
 *
 * This is the entry point for Stage 1. It:
 * 1. Builds parse context (reuses V1's buildParseContext)
 * 2. Finds <w:body> in document.xml
 * 3. Iterates body children, dispatching to sub-builders
 * 4. Returns a DocumentIR with all OOXML semantics resolved
 */

import type { DocumentIR, BlockIR } from '../irTypes';
import type { ParseContext } from '../../ooxmlParser/types';
import type { DocxPackage } from '../../ooxmlParser/zipReader';
import { buildParseContext } from '../../ooxmlParser/context';
import { getChildNS, W_NS } from '../../ooxmlParser/zipReader';
import { buildParagraphIR } from './paragraphIRBuilder';

// V1 delegations — used until V2 native implementations are built
import { parseTable as v1ParseTable } from '../../ooxmlParser/tableParser';
import { CommentTracker } from '../../ooxmlParser/commentParser';
import { TrackChangeTracker } from '../../ooxmlParser/trackChangeParser';

/**
 * Build a DocumentIR from a parsed DOCX package.
 */
export function buildDocumentIR(
  pkg: DocxPackage,
  generateNodeId?: () => string
): { ir: DocumentIR; ctx: ParseContext } {
  const ctx = buildParseContext(pkg, generateNodeId);
  const messages: string[] = [];

  // Set up V1 comment and track change trackers (delegated until V2 native implementation)
  const commentTracker = new CommentTracker(ctx);
  ctx.commentTracker = commentTracker;
  const trackChangeTracker = new TrackChangeTracker();
  ctx.trackChangeTracker = trackChangeTracker;

  // Find <w:body>
  const bodyEls = pkg.documentXml.getElementsByTagNameNS(W_NS, 'body');
  let bodyEl: Element | null = null;
  if (bodyEls.length > 0) {
    bodyEl = bodyEls[0];
  } else {
    // Fallback: try w:body without namespace
    const fallback = pkg.documentXml.getElementsByTagName('w:body');
    if (fallback.length > 0) bodyEl = fallback[0];
  }

  if (!bodyEl) {
    messages.push('Warning: No <w:body> found in document.xml');
    return {
      ir: {
        blocks: [],
        commentDefinitions: [],
        commentRanges: [],
        trackChanges: [],
        messages,
      },
      ctx,
    };
  }

  // Parse body content
  const blocks = parseBodyContent(bodyEl, ctx, messages);

  const ir: DocumentIR = {
    blocks,
    commentDefinitions: [], // Phase 5 native IR
    commentRanges: [], // Phase 5 native IR
    trackChanges: [], // Phase 5 native IR
    messages,
    // V1-delegated results (stored temporarily for Stage 2 to extract)
    _v1Comments: commentTracker.getParsedComments(),
    _v1TrackChanges: trackChangeTracker.getParsedTrackChanges(),
    _v1HasTrackChanges: trackChangeTracker.getParsedTrackChanges().length > 0,
  } as DocumentIR & { _v1Comments: any; _v1TrackChanges: any; _v1HasTrackChanges: boolean };

  return { ir, ctx };
}

/**
 * Parse the children of <w:body> (or any block-level container) into BlockIR nodes.
 */
export function parseBodyContent(
  container: Element,
  ctx: ParseContext,
  messages: string[]
): BlockIR[] {
  const blocks: BlockIR[] = [];
  const children = container.childNodes;

  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.nodeType !== 1) continue;

    const el = child as Element;
    const localName = el.localName || el.nodeName.replace(/^w:/, '');

    switch (localName) {
      case 'p': {
        const paraIR = buildParagraphIR(el, ctx);
        blocks.push(paraIR);
        break;
      }

      case 'tbl': {
        // Delegate to V1's table parser until V2 table IR is built.
        // The resulting PMNode is wrapped in a PassthroughBlockIR.
        const tableNode = v1ParseTable(el, ctx);
        blocks.push({ kind: 'passthrough', pmNode: tableNode });
        break;
      }

      case 'sdt': {
        // Structured Document Tag — unwrap and parse content
        const sdtBody = getChildNS(el, W_NS, 'sdtContent');
        if (sdtBody) {
          const sdtBlocks = parseBodyContent(sdtBody, ctx, messages);
          blocks.push(...sdtBlocks);
        }
        break;
      }

      case 'sectPr':
        // Section properties — handled separately for headers/footers
        break;

      case 'commentRangeStart':
      case 'commentRangeEnd':
        // Phase 5: comment support at body level
        break;

      case 'bookmarkStart':
      case 'bookmarkEnd':
        // Ignored
        break;

      default:
        // Skip unknown elements
        break;
    }
  }

  return blocks;
}
